import { Box } from '@mui/material';
import React from 'react';
import './index.scss';

interface EmptyStateProps {
  /** A 24px MUI icon element, matching how CRMPageTemplate and SectionCard take theirs. */
  icon?: React.ReactNode;
  /** What is not here. A noun phrase, not a sentence: "No signed agreements yet". */
  title: string;
  /** Why, or what to do about it. Optional, because sometimes there is nothing to say. */
  body?: React.ReactNode;
  /** Primary first. Rendered as given, so the caller keeps control of button variants. */
  actions?: React.ReactNode;
  /** Tighter padding, for an empty state inside a card rather than filling a page. */
  compact?: boolean;
  className?: string;
}

/**
 * The one empty state.
 *
 * Twelve pages had built their own before this existed: `.ma-empty`, `.an-empty`, `.ea-empty`,
 * `.notif-empty`, `.eo-list__empty`, `.pb-empty`, `.cc-skill__empty`, and five more that were a
 * bare `<Typography color="text.secondary">` in the middle of a card. They disagreed on padding,
 * on whether there was an icon, on whether the title was bolder than the body, and on which of
 * `--tx-low` / `--tx-mid` / MUI's `text.secondary` the text took — the last of which is not even
 * a token.
 *
 * <p>Twelve is not an accident of neglect. There was no shared one to reach for, and an empty
 * state is three elements, so writing it inline is always faster than looking. The component
 * existing is what makes looking the faster option.
 *
 * <p>The employer-facing site has had this component for a while and it is the model here, minus
 * the removable filter chips: that repo's version can hand back the filters that caused a
 * no-results screen, which is worth having but belongs with a filter mechanism this console has
 * not settled yet (`FilterRow` / `FilterBar` / `SearchFilter` are three answers to one question).
 * Add it here when that is decided, not before.
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  body,
  actions,
  compact = false,
  className,
}) => (
  <Box
    className={`cc-empty${compact ? ' cc-empty--compact' : ''}${className ? ` ${className}` : ''}`}
  >
    {icon && <span className="cc-empty__icon">{icon}</span>}
    <p className="cc-empty__title">{title}</p>
    {body && <p className="cc-empty__body">{body}</p>}
    {actions && <div className="cc-empty__actions">{actions}</div>}
  </Box>
);

export default EmptyState;
